import React, { useContext, useEffect, useState } from 'react';
import { ApiContext } from '@api/api';
import CustomsStatusTag from '@app/pages/Customs/tags/CustomsStatusTag.tsx';
import { CustomsCustomsInDTO, CustomsDetailOutDTO, PermissionTypesEnum } from '@api/ringil-api.v2.tsx';
import { isNotNullOrUndefined, isNullOrUndefined } from '@utils/utils.tsx';
import CustomsValuesTag from '@app/pages/Customs/tags/CustomsValueTag.tsx';
import CustomsMRNsTag from '@app/pages/Customs/tags/CustomsMRNTag.tsx';
import CustomsAreaTag from '@app/pages/Customs/tags/CustomsAreaTag.tsx';
import CustomsLoadingNumberTag from '@app/pages/Customs/tags/CustomsLoadingNumberTag.tsx';
import CustomsInternalPDNumberTag from '@app/pages/Customs/tags/CustomsInternalPDNumberTag.tsx';
import InternalNoteTag from '@components/framework/tags/InternalNoteTag.tsx';
import InvoicePanel from '@app/pages/Customs/panels/InvoicePanel.tsx';
import { Ringil3BadgeRow } from '@components/ringil3/styles.tsx';
import { SecuredConfig, useSecuredDecoration } from '@components/security/securedDecoration';
import { useSecuredFlag } from '@components/security/useSecuredFlag';
import SecuredArea from '@components/security/SecuredArea';

interface ComponentProps {
  expeditionId?: string;
  supplierNotificationId?: string;
  applicationId: string;
  readonly?: boolean;
  secured?: SecuredConfig;
}

const CustomsDetail: React.FC<ComponentProps> = ({ expeditionId, supplierNotificationId, applicationId, readonly = false, secured }) => {
  const {
    updateCustomsForExpedition,
    getCustomsForExpedition,
    getCustomsForSupplierNotification,
    updateCustomsForSupplierNotification,
  } =
    useContext(ApiContext);
  const [detail, setDetail] = useState<CustomsDetailOutDTO>();

  const showPriceFlag = useSecuredFlag('customs-show-price', {
    kind: 'visibility',
    description: 'Show prices in customs invoice panel',
    permissions: [PermissionTypesEnum.PERMISSION_EXPEDITION_PRICE],
  });
  const editCustomsFlag = useSecuredFlag('customs-edit', {
    kind: 'editability',
    description: 'Edit customs fields',
    permissions: [PermissionTypesEnum.PERMISSION_EXPEDITION_DETAIL_TAB_CUSTOMS_RW],
  });
  const showShipmentPricePermission = showPriceFlag.granted;
  const editCustomsPermission = !readonly && editCustomsFlag.granted;
  const customsEditable = [
    editCustomsFlag,
    // A lock set by the page, not "nothing to show": as `data` it would never reach the AI assistant.
    {
      type: 'state' as const,
      label: '!readonly (component prop)',
      satisfied: !readonly,
      description:
        'Read-only when the page locks the customs details: a shipment with the Imported status, or a DXX supplier notification for a user without the customs edit permission (PERMISSION_SN_DXX_DETAIL_TAB_CUSTOMS_RW)',
    },
  ];
  const { decorate } = useSecuredDecoration(secured);

  useEffect(() => {
    if (expeditionId) {
      getCustomsForExpedition(expeditionId, d => {
        if (isNullOrUndefined(d)) {
          setDetail({
            customsArea: null,
            customsValue: null,
            loadingNumber: null,
            id: null,
            status: null,
            entityId: null,
            entityType: null,
            internalNote: null,
            internalPDNumber: null,
            mrnNumber: null,
          });
        } else setDetail(d);
      });
    }

    if (supplierNotificationId) {
      getCustomsForSupplierNotification(supplierNotificationId, d => {
        if (isNullOrUndefined(d)) {
          setDetail({
            customsArea: null,
            customsValue: null,
            loadingNumber: null,
            id: null,
            status: null,
            entityId: null,
            entityType: null,
            internalNote: null,
            internalPDNumber: null,
            mrnNumber: null,
          });
        } else setDetail(d);
      });
    }
  }, [expeditionId, supplierNotificationId]);

  if (isNullOrUndefined(detail)) return <></>;

  const save = (key: keyof CustomsCustomsInDTO, v: any) => {
    if (isNotNullOrUndefined(expeditionId)) {
      updateCustomsForExpedition(
        expeditionId,
        expeditionId,
        {
          [key]: v,
        },
        () => {
          getCustomsForExpedition(expeditionId, d => {
            setDetail(d);
          });
        },
      );
    }

    if (isNotNullOrUndefined(supplierNotificationId)) {
      updateCustomsForSupplierNotification(
        supplierNotificationId,
        supplierNotificationId,
        {
          [key]: v,
        },
        () => {
          getCustomsForSupplierNotification(supplierNotificationId, d => {
            setDetail(d);
          });
        },
      );
    }
  };

  return (
    <>{decorate(<>
      <Ringil3BadgeRow>
        <CustomsStatusTag
          value={
            isNotNullOrUndefined(detail.status)
              ? {
                  status: detail.status.customsStatus,
                  localDate: detail.status.date?.customsLocalDate,
                }
              : null
          }
          onChange={v => {
            save(
              'customsStatus',
              isNotNullOrUndefined(v)
                ? {
                    customsStatus: v.status,
                    date: isNotNullOrUndefined(v.localDate) ? { customsLocalDate: v.localDate } : null,
                  }
                : null,
            );
          }}
          secured={{ id: 'customs-status', label: 'CustomsStatusTag', description: 'Customs status and the date it was set', editable: customsEditable }}
        />
        <CustomsValuesTag value={detail.customsValue} onChange={v => save('customsValue', v)}
                          secured={{ id: 'customs-value', label: 'CustomsValuesTag', description: 'Customs value of the goods', editable: customsEditable }} />
        <CustomsMRNsTag value={detail.mrnNumber} onChange={v => save('mrnNumber', { mrnNumber: v })}
                        secured={{ id: 'customs-mrn', label: 'CustomsMRNsTag', description: 'MRN numbers of the customs declarations', editable: customsEditable }} />
        <CustomsAreaTag value={detail.customsArea} onChange={v => save('customsArea', { customsArea: v })}
                        secured={{ id: 'customs-area', label: 'CustomsAreaTag', description: 'Customs area', editable: customsEditable }} />
      </Ringil3BadgeRow>
      <Ringil3BadgeRow className={'flex flex-row gap-4'}>
        <CustomsLoadingNumberTag value={detail.loadingNumber}
                                 onChange={v => save('loadingNumber', { loadingNumber: v })}
                                 secured={{ id: 'customs-loading-number', label: 'CustomsLoadingNumberTag', description: 'Loading number for customs', editable: customsEditable }} />
        <CustomsInternalPDNumberTag value={detail.internalPDNumber}
                                    onChange={v => save('internalPDNumber', { internalPDNumber: v })}
                                    secured={{ id: 'customs-internal-pd-number', label: 'CustomsInternalPDNumberTag', description: 'Internal customs declaration (PD) number', editable: customsEditable }} />
        <InternalNoteTag value={detail.internalNote} onChange={v => save('internalNote', { internalNote: v })}
                         secured={{ id: 'customs-internal-note', label: 'InternalNoteTag', description: 'Internal note on the customs details', editable: customsEditable }} />
      </Ringil3BadgeRow>
      <SecuredArea
        id="customs-invoice-panel-expedition"
        label="InvoicePanel (expedition)"
        description="Customs invoices of the shipment"
        visible={[{ type: 'data', label: 'expeditionId present', satisfied: isNotNullOrUndefined(expeditionId) }]}
        editable={customsEditable}
      >
        <InvoicePanel expeditionId={expeditionId} applicationId={applicationId} readOnly={!editCustomsPermission}
                      showPrices={showShipmentPricePermission} />
      </SecuredArea>
      <SecuredArea
        id="customs-invoice-panel-sn"
        label="InvoicePanel (supplier notification)"
        description="Customs invoices of the supplier notification"
        visible={[{ type: 'data', label: 'supplierNotificationId present', satisfied: isNotNullOrUndefined(supplierNotificationId) }]}
      >
        <InvoicePanel supplierNotificationId={supplierNotificationId} applicationId={applicationId} />
      </SecuredArea>
    </>)}</>
  );
};

export default CustomsDetail;
